import * as React from 'react';
import axios from 'axios';
import { Formik, Form } from 'formik';
import { toast } from 'react-toastify';
import {
  TextField,
  Button,
  Stack,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Autocomplete,
  Avatar,
  Typography,
  Container,
  Card,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Add, RemoveCircle } from '@mui/icons-material';
import * as Yup from 'yup';
import moment from 'moment';
import avatar from '../assets/avatar.png';
import Page from '../components/Page';
import PageHeader from '../components/smallComponents/PageHeader';

const AddToolsToProject = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = React.useState([]);
  const [open3, setOpen3] = React.useState(false);
  const [supervisors, setSupervisors] = React.useState([]);
  const [tools, setTools] = React.useState([{}]);

  const validationSchema = Yup.object({
    tools: Yup.array().required('Tools are required'),
    projectId: Yup.string().required('Project is required'),
    date: Yup.date().required('Date is required'),
    description: Yup.string(),
  });

  const getProjects = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/project/list`, {
        params: {
          statuses: ['APPROVED', 'ONGOING'],
        },
      });
      console.log(res);
      if (res.data.isError === false) {
        setProjects(res.data.data);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getSupervisors = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/member/employee/active/all`, {
        params: {
          role: 'SUPERVISOR',
        },
      });
      console.log(res.data.data);
      setSupervisors(res.data.data);
    } catch (error) {
      console.log(error);
    }
  };

  const getTools = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/tool/active`);
      console.log(res);
      if (res.data.isError === false) {
        setTools(res.data.data);
      }
    } catch (error) {
      console.log(error);
    }
  };

  React.useEffect(() => {
    getProjects();
    getSupervisors();
    getTools();
  }, []);

  return (
    <Page title="Add Journal">
      <Container maxWidth="xl">
        <PageHeader title="Add Tools to Project" />
        <Card>
          <Box m={2}>
            <Formik
              initialValues={{
                tools: [
                  {
                    toolId: '',
                    quantity: '',
                  },
                ],
                projectId: '',
                date: moment().format('YYYY-MM-DD'),
                description: '',
                supervisor: '',
              }}
              validationSchema={validationSchema}
              onSubmit={async (values, actions) => {
                try {
                  const response = await axios.post(
                    `${process.env.REACT_APP_API_URL}/toolFlowLog/addMultipleToProject`,
                    values
                  );
                  actions.setSubmitting(false);
                  toast.success(response.data.message);
                  navigate('/dashboard/warehouse/tool');
                } catch (error) {
                  console.log(error);
                  actions.setSubmitting(false);
                  toast.error(error.response.data.message);
                }
              }}
            >
              {(formik) => (
                <Form>
                  <Stack spacing={2} mt={2}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <Autocomplete
                        id="asynchronous-demo"
                        fullWidth
                        open={open3}
                        onOpen={() => {
                          setOpen3(true);
                        }}
                        onClose={() => {
                          setOpen3(false);
                        }}
                        isOptionEqualToValue={(option, value) => option.name === value.name}
                        getOptionLabel={(option) => option.name}
                        options={projects}
                        onChange={(_, value) => {
                          if (value) {
                            formik.setFieldValue('projectId', value._id);
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            required
                            {...params}
                            error={Boolean(formik.touched.projectId && formik.errors.projectId)}
                            helperText={formik.touched.projectId && formik.errors.projectId}
                            label="Project"
                            InputProps={{
                              ...params.InputProps,
                            }}
                          />
                        )}
                      />
                      <TextField
                        fullWidth
                        id="date"
                        name="date"
                        label="Date"
                        sx={{ width: '220px' }}
                        type="date"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        {...formik.getFieldProps('date')}
                        error={formik.touched.date && Boolean(formik.errors.date)}
                        helperText={formik.touched.date && formik.errors.date}
                      />
                    </Stack>

                    <Autocomplete
                      fullWidth
                      id="asynchronous-demo"
                      onChange={(_, value) => {
                        console.log('Selected Value:', value);
                        formik.setFieldValue('supervisor', value ? value._id : null);
                      }}
                      isOptionEqualToValue={(option, value) => option.fullname === value.fullname}
                      getOptionLabel={(option) => option.fullname}
                      getOptionSelected={(option, value) => option.fullname === value.fullname}
                      options={supervisors}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Supervisor"
                          InputProps={{
                            ...params.InputProps,
                          }}
                        />
                      )}
                      renderOption={(props, option) => (
                        <li {...props}>
                          <Stack direction="row" spacing={2} alignItems="center">
                            <Avatar
                              sx={{ width: 40, height: 40, cursor: 'pointer' }}
                              src={(option?.profilePic && process.env.REACT_APP_API_URL + option.profilePic) || avatar}
                            />
                            <Stack direction={'column'}>
                              <Typography variant="body2" sx={{ fontWeight: 'bold', lineHeight: '1.1' }}>
                                {option.fullname}
                              </Typography>
                              <Typography variant="caption" sx={{ lineHeight: '1.1' }}>
                                {option.role?.name?.replaceAll('_', ' ')}
                              </Typography>
                            </Stack>
                          </Stack>
                        </li>
                      )}
                    />

                    <TextField
                      fullWidth
                      multiline
                      id="description"
                      name="description"
                      label="Description"
                      {...formik.getFieldProps('description')}
                      error={formik.touched.description && Boolean(formik.errors.description)}
                      helperText={formik.touched.description && formik.errors.description}
                    />
                    {formik.values.tools.map((tool, index) => (
                      <Stack key={index} direction="row" spacing={2} alignItems="center">
                        <Typography sx={{ fontWeight: 'bold' }}>{index + 1}.</Typography>
                        <Autocomplete
                          fullWidth
                          id="asynchronous-demo"
                          onChange={(_, value) => {
                            formik.setFieldValue(`tools[${index}].toolId`, value._id);
                          }}
                          isOptionEqualToValue={(option, value) => option.name === value.name}
                          getOptionLabel={(option) => `${option.name} (${option.unitsAvailable} units available) `}
                          options={tools}
                          renderInput={(params) => (
                            <TextField
                              required
                              {...params}
                              error={Boolean(formik.touched.tools && formik.errors.tools)}
                              helperText={formik.touched.tools && formik.errors.tools}
                              label="Tool"
                              InputProps={{
                                ...params.InputProps,
                              }}
                            />
                          )}
                          renderOption={(props, option) => (
                            <li {...props}>
                              <Stack direction="row" spacing={2} alignItems="center">
                                <Avatar
                                  sx={{ width: 40, height: 40, cursor: 'pointer' }}
                                  src={option?.image && process.env.REACT_APP_API_URL + option.image}
                                >
                                  {option?.image ? '' : option.name.charAt(0).toUpperCase()}
                                </Avatar>

                                <Stack direction={'column'}>
                                  <Typography variant="body2" sx={{ fontWeight: 'bold', lineHeight: '1.1' }}>
                                    {option.name}
                                  </Typography>
                                  <Stack direction="row" spacing={0.5} alignItems="center">
                                    <Typography variant="caption" sx={{ lineHeight: '1.1' }}>
                                      Avaialable Units:
                                    </Typography>
                                    <Typography variant="caption" sx={{ lineHeight: '1.1' }}>
                                      {option.unitsAvailable}
                                    </Typography>
                                  </Stack>
                                </Stack>
                              </Stack>
                            </li>
                          )}
                        />
                        <TextField
                          id="quantity"
                          name="quantity"
                          sx={{
                            width: '150px',
                          }}
                          inputProps={{ min: 1, max: tool.unitsAvailable }}
                          label="Quantity"
                          type="number"
                          {...formik.getFieldProps(`tools[${index}].quantity`)}
                          error={formik.touched.quantity && Boolean(formik.errors.quantity)}
                          helperText={formik.touched.quantity && formik.errors.quantity}
                        />
                        <RemoveCircle
                          sx={{
                            cursor: 'pointer',
                          }}
                          color="error"
                          onClick={() => {
                            formik.setFieldValue(
                              'tools',
                              formik.values.tools.filter((_, i) => i !== index)
                            );
                          }}
                        />
                      </Stack>
                    ))}
                    <Stack direction="row" spacing={2} alignItems="center" justifyContent="end">
                      <Button
                        variant="outlined"
                        onClick={() => {
                          formik.setFieldValue('tools', [
                            ...formik.values.tools,
                            {
                              toolId: '',
                              quantity: '',
                            },
                          ]);
                        }}
                        startIcon={<Add />}
                      >
                        Tool
                      </Button>
                    </Stack>
                    <Stack justifyContent={'center'} alignItems="center" direction={'row'} width={'100%'}>
                      <Button type="submit" variant="contained" disabled={formik.isSubmitting} sx={{ width: '100px' }}>
                        Submit
                      </Button>
                    </Stack>
                  </Stack>
                </Form>
              )}
            </Formik>
          </Box>
        </Card>
      </Container>
    </Page>
  );
};

export default AddToolsToProject;
